// src/pages/DashboardHome.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  Users, TrendingUp, UserCheck, Clock, Megaphone, CheckSquare,
  Trash2, Plus, BarChart3, AlertCircle, LogIn, LogOut, Timer, AlertTriangle, CheckCircle2, RefreshCw
} from "lucide-react";
import "./dashboard.css";

function daysAgo(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return Infinity;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function urgencyLabel(days) {
  if (days >= 8) return { label: "Overdue", cls: "urgent" };
  if (days === 7) return { label: "Today!", cls: "today" };
  return { label: `${days}d`, cls: "soon" };
}

function getInitials(name) {
  if (!name) return "?";
  const p = name.trim().split(/\s+/);
  return p.length === 1 ? p[0][0].toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

const DashboardHome = () => {
  const { user } = useAuth();
  const [leads, setLeads]               = useState([]);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [tasks, setTasks]               = useState([]);
  const [newAnnounce, setNewAnnounce]   = useState("");
  const [newTask, setNewTask]           = useState("");
  const [posting, setPosting]           = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [refreshingAttendance, setRefreshingAttendance] = useState(false);
  const announceRef = useRef(null);

  // ── Live attendance loader with background polling ────────
  const loadAttendance = useCallback(async (showToast = false) => {
    if (!user || (user.role !== "Admin" && user.role !== "Manager")) return;
    setRefreshingAttendance(true);
    try {
      const res = await api.get("/attendance/today-summary");
      setAttendanceSummary(Array.isArray(res.data) ? res.data : []);
      if (showToast) toast.success("Live attendance refreshed");
    } catch {
      // silent
    } finally {
      setRefreshingAttendance(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user || (user.role !== "Admin" && user.role !== "Manager")) return;
    loadAttendance();
    const timer = setInterval(() => loadAttendance(), 25000); // 25s auto-refresh
    return () => clearInterval(timer);
  }, [user, loadAttendance]);

  // ── Data loading ──────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // 1. Fast aggregated Dashboard Summary (instant, sub-50ms)
    api.get("/leads/dashboard-summary").then((r) => {
      if (r.data?.stats) {
        setDashboardSummary(r.data);
      }
    }).catch(() => {
      // Fallback if summary endpoint is unreachable
      api.get("/leads?limit=500").then((r) => {
        const data = Array.isArray(r.data) ? r.data : (r.data.leads || []);
        setLeads(data);
      }).catch(() => {});
    });

    // 2. Announcements
    api.get("/announcements").then((r) => setAnnouncements(Array.isArray(r.data) ? r.data : [])).catch(() => {});

    // 3. My Tasks (strictly scoped to current user: scope=mine)
    api.get("/tasks?scope=mine").then((r) => setTasks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user]);

  // ── Stats ────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (dashboardSummary?.stats) {
      return dashboardSummary.stats;
    }
    const total      = leads.length;
    const customers  = leads.filter((l) => l.status === "Customer").length;
    const followups  = leads.filter((l) => l.status === "Follow-up").length;
    const conv       = leads.filter((l) => l.status === "In Conversation").length;
    const notInt     = leads.filter((l) => l.status === "Not Interested").length;
    const conversion = total > 0 && !isNaN(customers) ? Math.round((Number(customers) / Number(total)) * 100) : 0;
    return { total, customers, followups, conv, notInt, conversion: isNaN(conversion) ? 0 : conversion };
  }, [dashboardSummary, leads]);

  // ── Follow-ups near expiry (6+ days since remarksDate) ───
  const expiring = useMemo(() => {
    if (dashboardSummary?.expiring) {
      return dashboardSummary.expiring.map((l) => ({ ...l, _days: daysAgo(l.remarksDate) }));
    }
    if (!user || user.role === "Admin") return [];
    let myLeads = leads;
    if (user.role === "Agent") myLeads = leads.filter(l => String(l.agentId) === String(user.id));
    if (user.role === "Manager") myLeads = leads.filter(l => String(l.agentId) === String(user.id) || String(l.managerId) === String(user.id));

    return myLeads
      .filter((l) => ["Follow-up", "In Conversation"].includes(l.status))
      .map((l) => ({ ...l, _days: daysAgo(l.remarksDate) }))
      .filter((l) => l._days >= 6)
      .sort((a, b) => b._days - a._days)
      .slice(0, 8);
  }, [dashboardSummary, leads, user]);

  const unassignWarning = useMemo(() => {
    if (dashboardSummary?.unassignWarning) {
      return dashboardSummary.unassignWarning.map((l) => ({ ...l, _assignedDays: daysAgo(l.assignedAt || l.createdAt) }));
    }
    if (!user || user.role === "Admin") return [];
    let myLeads = leads;
    if (user.role === "Agent") myLeads = leads.filter(l => String(l.agentId) === String(user.id));
    if (user.role === "Manager") myLeads = leads.filter(l => String(l.agentId) === String(user.id) || String(l.managerId) === String(user.id));

    return myLeads
      .filter((l) => l.agentId && l.status !== 'Customer')
      .map((l) => ({ ...l, _assignedDays: daysAgo(l.assignedAt || l.createdAt) }))
      .filter((l) => l._assignedDays >= 5 && l._assignedDays < 7)
      .sort((a, b) => b._assignedDays - a._assignedDays)
      .slice(0, 8);
  }, [dashboardSummary, leads, user]);

  // ── Actions ──────────────────────────────────────────────
  const postAnnouncement = useCallback(async () => {
    if (!newAnnounce.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/announcements", { text: newAnnounce.trim() });
      setAnnouncements((p) => [data, ...p]);
      setNewAnnounce("");
      announceRef.current?.focus();
      toast.success("Announcement posted!");
    } catch { toast.error("Failed to post"); }
    finally { setPosting(false); }
  }, [newAnnounce]);

  const deleteAnnouncement = useCallback(async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      setAnnouncements((p) => p.filter((a) => (a.id || a._id) !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  }, []);

  const addTask = useCallback(async () => {
    if (!newTask.trim()) return;
    try {
      const { data } = await api.post("/tasks", {
        title: newTask.trim(), assignedTo: user.id, status: "Open", priority: "Medium",
      });
      setTasks((p) => [data, ...p]);
      setNewTask("");
      toast.success("Task added!");
    } catch { toast.error("Failed to add task"); }
  }, [newTask, user]);

  const toggleTaskStatus = useCallback(async (task) => {
    const newStatus = task.status === "Completed" ? "Open" : "Completed";
    try {
      await api.put(`/tasks/${task.id || task._id}`, { status: newStatus });
      setTasks(p => p.map(t => (t.id || t._id) === (task.id || task._id) ? { ...t, status: newStatus } : t));
    } catch {
      toast.error("Failed to update task");
    }
  }, []);

  const deleteTask = useCallback(async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks((p) => p.filter((t) => (t.id || t._id) !== id));
      toast.success("Task deleted");
    } catch { toast.error("Failed to delete task"); }
  }, []);

  if (!user) return <div className="crm-page" style={{ color: "var(--muted)" }}>Loading…</div>;

  return (
    <div className="crm-page dash-page">

      {/* ── Header ── */}
      <div className="dash-header">
        <h2>Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user.name?.split(" ")[0]}</h2>
        <div className="dash-header-sub">Here's your CRM overview for today.</div>
      </div>

      {/* ── Stat cards (Apple Pro Studio) ── */}
      <div className="dash-stats" data-tour="dashboard-stats">
        <div className="stat-card accent-blue">
          <div className="stat-card__top">
            <span className="stat-card__label">Total Leads</span>
            <div className="stat-card__icon-wrap">
              <BarChart3 size={16} />
            </div>
          </div>
          <div className="stat-card__bottom">
            <div className="stat-card__value">{stats.total}</div>
            <span className="stat-card__tag">Active</span>
          </div>
        </div>

        <div className="stat-card accent-green">
          <div className="stat-card__top">
            <span className="stat-card__label">Customers</span>
            <div className="stat-card__icon-wrap">
              <UserCheck size={16} />
            </div>
          </div>
          <div className="stat-card__bottom">
            <div className="stat-card__value">{stats.customers}</div>
            <span className="stat-card__tag positive">Converted</span>
          </div>
        </div>

        <div className="stat-card accent-orange">
          <div className="stat-card__top">
            <span className="stat-card__label">Follow-ups</span>
            <div className="stat-card__icon-wrap">
              <Clock size={16} />
            </div>
          </div>
          <div className="stat-card__bottom">
            <div className="stat-card__value">{stats.followups}</div>
            <span className="stat-card__tag warning">Pending</span>
          </div>
        </div>

        <div className="stat-card accent-purple">
          <div className="stat-card__top">
            <span className="stat-card__label">In Conversation</span>
            <div className="stat-card__icon-wrap">
              <TrendingUp size={16} />
            </div>
          </div>
          <div className="stat-card__bottom">
            <div className="stat-card__value">{stats.conv}</div>
            <span className="stat-card__tag">Engaged</span>
          </div>
        </div>

        <div className="stat-card accent-cyan">
          <div className="stat-card__top">
            <span className="stat-card__label">Conversion Rate</span>
            <div className="stat-card__icon-wrap">
              <Users size={16} />
            </div>
          </div>
          <div className="stat-card__bottom">
            <div className="stat-card__value">{stats.conversion}%</div>
            <span className="stat-card__tag rate">Ratio</span>
          </div>
        </div>

        <div className="stat-card accent-red">
          <div className="stat-card__top">
            <span className="stat-card__label">Not Interested</span>
            <div className="stat-card__icon-wrap">
              <AlertCircle size={16} />
            </div>
          </div>
          <div className="stat-card__bottom">
            <div className="stat-card__value">{stats.notInt}</div>
            <span className="stat-card__tag">Closed</span>
          </div>
        </div>
      </div>

      {/* ── Admin/Manager Attendance Widget ── */}
      {(user?.role === 'Admin' || user?.role === 'Manager') && attendanceSummary.length > 0 && (
        <div className="dash-box mb-4" style={{ backgroundColor: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
          <div className="dash-box__header d-flex justify-content-between align-items-center" style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
            <h5 className="mb-0 fw-bold d-flex align-items-center gap-2"><Timer size={18} className="text-primary"/> Today's Team Attendance</h5>
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '6px', fontSize: '12px', padding: '4px 10px' }}
              onClick={() => loadAttendance(true)}
              disabled={refreshingAttendance}
              title="Refresh live team presence"
            >
              <RefreshCw size={13} className={refreshingAttendance ? 'crm-spinner' : ''} />
              <span>{refreshingAttendance ? 'Updating...' : 'Live Refresh'}</span>
            </button>
          </div>
          <div className="dash-box__body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ color: 'var(--text)' }}>
                <thead style={{ backgroundColor: 'var(--surface-2)' }}>
                  <tr>
                    <th className="border-0 px-4 py-3 text-muted small fw-bold">Employee</th>
                    <th className="border-0 py-3 text-muted small fw-bold">Status</th>
                    <th className="border-0 py-3 text-muted small fw-bold">Login Time</th>
                    <th className="border-0 py-3 text-muted small fw-bold">Total Work Hours</th>
                    <th className="border-0 py-3 text-muted small fw-bold">Logout Time</th>
                  </tr>
                </thead>
                <tbody style={{ borderTop: 'none' }}>
                  {attendanceSummary.map(emp => {
                    const checkIn = emp.checkInAt ? new Date(emp.checkInAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '--';
                    const checkOut = emp.checkOutAt ? new Date(emp.checkOutAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '--';
                    const hrs = emp.workingMins ? (emp.workingMins / 60).toFixed(1) : '0.0';
                    return (
                      <tr key={emp.id} style={{ borderColor: 'var(--border)' }}>
                        <td className="px-4 py-3 fw-semibold">{emp.name}</td>
                        <td className="py-3">
                          {emp.onlineStatus === 'online' ? (
                            <span className="badge rounded-pill" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid rgba(48, 209, 88, 0.25)', padding: '4px 10px', fontSize: '11px', fontWeight: 600 }}>Online</span>
                          ) : emp.onlineStatus === 'break' ? (
                            <span className="badge rounded-pill" style={{ background: 'var(--warning-bg)', color: 'var(--warning)', border: '1px solid rgba(255, 159, 10, 0.25)', padding: '4px 10px', fontSize: '11px', fontWeight: 600 }}>On Break</span>
                          ) : (
                            <span className="badge rounded-pill" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)', padding: '4px 10px', fontSize: '11px', fontWeight: 600 }}>Offline</span>
                          )}
                        </td>
                        <td className="py-3"><LogIn size={14} className="me-1 opacity-50"/> {checkIn}</td>
                        <td className="py-3 fw-bold text-primary">{hrs} hrs</td>
                        <td className="py-3"><LogOut size={14} className="me-1 opacity-50"/> {checkOut}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom panels ── */}
      <div className="dash-bottom">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-tour="dashboard-alerts">
          {/* Follow-ups expiring */}
          <div className="dash-panel">
            <div className="dash-panel__head">
              <span className="dash-panel__title d-flex align-items-center gap-2">
                <AlertTriangle size={15} style={{ color: 'var(--warning)' }} /> Follow-ups Near Expiry
              </span>
              <span style={{ background: "var(--danger-bg)", color: "var(--danger)", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                {expiring.length}
              </span>
            </div>
            <div className="dash-panel__body">
              {expiring.length === 0 ? (
                <div className="dash-panel__empty">
                  <CheckCircle2 size={18} style={{ color: "var(--success)", display: "inline", marginRight: 8 }} />
                  All caught up! No overdue follow-ups.
                </div>
              ) : expiring.map((lead) => {
                const urg = urgencyLabel(lead._days);
                return (
                  <div key={lead.id || lead._id} className="followup-item">
                    <div className="followup-avatar">{getInitials(lead.name)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="followup-name">{lead.name}</div>
                      <div className="followup-date">
                        Agent: {lead.agentName || "—"} · Last: {lead.remarksDate ? new Date(lead.remarksDate).toLocaleDateString() : "Never"}
                      </div>
                    </div>
                    <span className={`followup-badge ${urg.cls}`}>{urg.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Leads Auto-Unassigning Soon */}
          <div className="dash-panel">
            <div className="dash-panel__head">
              <span className="dash-panel__title d-flex align-items-center gap-2">
                <Clock size={15} style={{ color: 'var(--warning)' }} /> Leads Expiring Soon
              </span>
              <span style={{ background: "var(--warning-bg)", color: "var(--warning)", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                {unassignWarning.length}
              </span>
            </div>
            <div className="dash-panel__body">
              {unassignWarning.length === 0 ? (
                <div className="dash-panel__empty">
                  <CheckCircle2 size={18} style={{ color: "var(--success)", display: "inline", marginRight: 8 }} />
                  All good! No leads at risk of unassignment.
                </div>
              ) : unassignWarning.map((lead) => (
                <div key={lead.id || lead._id} className="followup-item">
                  <div className="followup-avatar" style={{ background: 'var(--warning)', color: '#000' }}>{getInitials(lead.name)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="followup-name">{lead.name}</div>
                    <div className="followup-date">
                      Agent: {lead.agentName || "—"} · Assigned {lead._assignedDays} days ago
                    </div>
                  </div>
                  <span className="followup-badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
                    {7 - lead._assignedDays} days left
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Tasks */}
          <div className="dash-panel">
            <div className="dash-panel__head">
              <span className="dash-panel__title"><CheckSquare size={15} style={{ marginRight: 6, verticalAlign: -2 }} />My Tasks</span>
              <span style={{ background: "var(--info-bg)", color: "var(--blue)", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{tasks.length}</span>
            </div>
            <div className="dash-panel__body">
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTask()}
                  placeholder="Add a task…"
                  style={{ flex: 1, padding: "7px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", fontSize: 13, outline: "none" }}
                />
                <button onClick={addTask} style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: "var(--primary)", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={15} />
                </button>
              </div>
              {tasks.length === 0 ? <div className="dash-panel__empty">No tasks yet.</div> : (
                tasks.slice(0, 8).map((t) => {
                  const isDone = t.status === "Completed";
                  const canDelete = user.role === "Admin" || String(t.assignedTo) === String(user.id) || String(t.createdBy) === String(user.id);
                  return (
                    <div key={t.id || t._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--border-2)" }}>
                      <button
                        onClick={() => toggleTaskStatus(t)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          padding: 0,
                          color: isDone ? "var(--success)" : "var(--muted)",
                          display: "flex",
                          alignItems: "center"
                        }}
                        title={isDone ? "Mark Open" : "Mark Completed"}
                      >
                        <CheckCircle2 size={16} />
                      </button>
                      <span style={{
                        flex: 1,
                        fontSize: 13,
                        color: isDone ? "var(--muted)" : "var(--text-2)",
                        textDecoration: isDone ? "line-through" : "none",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {t.title || t.text}
                      </span>
                      <span
                        onClick={() => toggleTaskStatus(t)}
                        style={{
                          fontSize: 10.5,
                          fontWeight: 600,
                          cursor: "pointer",
                          color: isDone ? "var(--success)" : "var(--muted)",
                          flexShrink: 0,
                          background: isDone ? "var(--success-bg)" : "var(--surface-2)",
                          padding: "2px 7px",
                          borderRadius: 6,
                          border: isDone ? "1px solid rgba(48, 209, 88, 0.25)" : "1px solid var(--border)"
                        }}
                        title="Click to toggle status"
                      >
                        {t.status || "Open"}
                      </span>
                      {canDelete && (
                        <button
                          onClick={() => deleteTask(t.id || t._id)}
                          style={{ border: "none", background: "transparent", color: "var(--danger)", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", flexShrink: 0 }}
                          title="Delete task"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Announcements */}
          <div className="dash-panel" style={{ flex: 1 }}>
            <div className="dash-panel__head">
              <span className="dash-panel__title"><Megaphone size={15} style={{ marginRight: 6, verticalAlign: -2 }} />Announcements</span>
            </div>
            <div className="dash-panel__body" style={{ maxHeight: 220 }}>
              {announcements.length === 0 ? (
                <div className="dash-panel__empty">No announcements yet.</div>
              ) : announcements.map((a) => (
                <div key={a.id || a._id} className="announce-item">
                  <div className="announce-dot" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="announce-text">{a.text}</div>
                    <div className="announce-by">— {a.by} · {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ""}</div>
                  </div>
                  {user.role === "Admin" && (
                    <button onClick={() => deleteAnnouncement(a.id || a._id)} style={{ border: "none", background: "transparent", color: "var(--danger)", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", flexShrink: 0 }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {user.role === "Admin" && (
              <div className="announce-form">
                <input
                  ref={announceRef}
                  value={newAnnounce}
                  onChange={(e) => setNewAnnounce(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && postAnnouncement()}
                  placeholder="Write an announcement…"
                  className="announce-input"
                />
                <button onClick={postAnnouncement} disabled={posting} className="announce-btn">
                  Post
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;